import React from 'react';
import useUpdateEffect from './useUpdateEffect';
import {Dates} from '../utils';

// LibreWXR serves the RainViewer v2-compatible weather-maps schema, so the
// shapes below fit both APIs. Tile URLs must always be composed from the
// response `host` + item `path` — path formats are backend-controlled and
// have changed before (RainViewer moved from timestamp to hash paths).
export type RainViewerItem = {
  time: number;
  path: string;
};

export type RainViewerApiResponse = {
  version: string;
  generated: number;
  host: string;
  radar: {
    past: RainViewerItem[];
    nowcast: RainViewerItem[];
  };
  satellite: {
    infrared: RainViewerItem[];
  };
};

const WEATHER_MAPS_URL = 'https://radar.smartmixin.io/public/weather-maps.json';

const findClosestIndex = (target: number, timestamps: number[]) => {
  if (timestamps.length > 0) {
    const closest = timestamps.reduce((prev, curr) => {
      return Math.abs(curr - target) < Math.abs(prev - target) ? curr : prev;
    });
    return timestamps.findIndex((item) => closest === item);
  }
  return 0;
};

const REFRESH_INTERVAL_MS = 6 * 60 * 1000; // 6 minutes

// Watchdog for the metadata request. A fetch still in flight when the device
// suspends can hang indefinitely — without this the in-flight guard below would
// never be released and the hook would stop refreshing until a page reload.
const FETCH_TIMEOUT_MS = 15 * 1000;

// After a FAILED attempt, retry well before the normal interval. A wake-up
// fetch commonly fails because the network stack isn't up yet (the OS reports
// the app active before connectivity returns), and waiting a full 6 minutes
// leaves the map on frames the server has already dropped. Web additionally
// gets an `online` event; mobile has no equivalent without a new dependency,
// so this backoff is what covers it there.
const FAILED_RETRY_INTERVAL_MS = 30 * 1000;

/**
 * Timeline of OBSERVED radar frames only.
 *
 * The API also returns a `nowcast` array — frames extrapolated into the future
 * by optical flow blended with NWP. Those are computed, not measured, so they
 * are deliberately ignored: the slider shows real radar and nothing else.
 * (This also removes the `includeNowcast` argument that used to sit in the 4th
 * position, where the mobile map screen was accidentally passing its
 * radar-layer toggle.)
 */
const useRainRadar = (
  isFocused: boolean,
  initialPlaying: boolean,
  syncWithNow = true,
) => {
  const [timestamps, setTimestamps] = React.useState<number[]>([]);
  const [rainRadarItems, setRainRadarItems] =
    React.useState<Record<number, string>>();
  const [host, setHost] = React.useState<string>();
  const [index, setIndex] = React.useState<number>(
    syncWithNow ? findClosestIndex(Dates.now(), timestamps) : 0,
  );
  const [currentTimestamp, setCurrentTimestamp] = React.useState<number>();
  const [nextTimestamp, setNextTimestamp] = React.useState<number>();
  const [playing, setPlaying] = React.useState(initialPlaying);
  const [lastDataFetch, setLastDataFetch] = React.useState(Dates.now());

  const isUpdatingRef = React.useRef(false);

  // Mirrors of committed state, read inside fetchRadarData. They are refs (not
  // deps) so the callback identity stays stable — depending on them would reset
  // the refresh timer on every frame change.
  const timestampsRef = React.useRef<number[]>(timestamps);
  timestampsRef.current = timestamps;
  const indexRef = React.useRef(index);
  indexRef.current = index;
  const pastLengthRef = React.useRef(0);
  const hasLoadedRef = React.useRef(false);
  // Staleness is measured from the last SUCCESSFUL fetch, not from the last
  // attempt: a wake-up fetch that fails (the network stack is often not up yet
  // when `visibilitychange` fires) must not make the data look fresh and
  // disarm the `online` retry that follows a second later.
  const lastSuccessRef = React.useRef(Dates.now());
  const abortRef = React.useRef<AbortController | undefined>(undefined);
  // Monotonic id so a superseded fetch (aborted by a wake refresh) cannot
  // clobber the state of the one that replaced it.
  const fetchIdRef = React.useRef(0);
  const lastAttemptFailedRef = React.useRef(false);

  const fetchRadarData = React.useCallback(async () => {
    // Prevent concurrent calls
    if (isUpdatingRef.current) {
      return;
    }

    let controller: AbortController | undefined;
    let watchdog: ReturnType<typeof setTimeout> | undefined;

    // Where should the playhead land once the new frame list is in? Keeping the
    // user's frame matters because a refresh arrives every 6 minutes — resetting
    // to "now" each time would yank the slider out from under an active scrub.
    const resolveIndex = (
      nextTimestamps: number[],
      nextPastLength: number,
    ): number => {
      const liveEdge = Math.max(0, nextPastLength - 1);
      if (!hasLoadedRef.current) {
        return syncWithNow ? liveEdge : 0;
      }
      const prevPastLength = pastLengthRef.current;
      const prevIndex = indexRef.current;
      // Sitting on the newest observed frame → keep following the live edge.
      if (prevPastLength > 0 && prevIndex === prevPastLength - 1) {
        return liveEdge;
      }
      const prevTimestamp = timestampsRef.current[prevIndex];
      if (prevTimestamp == null) {
        return syncWithNow ? liveEdge : 0;
      }
      const stillServed = nextTimestamps.indexOf(prevTimestamp);
      if (stillServed !== -1) {
        return stillServed;
      }
      // The frame is gone. If it predates everything still served it rolled out
      // of the server's retention window (older frames 404) and the user's
      // moment is unrecoverable — go to the live edge rather than clamping them
      // to the oldest frame, which would still look like a stale map. Otherwise
      // it fell out of the middle of the list, so the nearest survivor is the
      // faithful choice.
      if (nextTimestamps.length > 0 && prevTimestamp < nextTimestamps[0]) {
        return syncWithNow ? liveEdge : 0;
      }
      return findClosestIndex(prevTimestamp, nextTimestamps);
    };

    isUpdatingRef.current = true;
    const fetchId = ++fetchIdRef.current;

    try {
      controller =
        typeof AbortController !== 'undefined'
          ? new AbortController()
          : undefined;
      abortRef.current = controller;

      // Raced against the watchdog rather than relying on the abort rejecting:
      // a connection suspended mid-flight may never settle either way.
      const response = (await Promise.race([
        fetch(
          WEATHER_MAPS_URL,
          controller ? {signal: controller.signal} : undefined,
        ),
        new Promise<never>((_, reject) => {
          watchdog = setTimeout(() => {
            controller?.abort();
            reject(new Error('weather-maps request timed out'));
          }, FETCH_TIMEOUT_MS);
        }),
      ])) as Response;

      if (!response.ok) {
        lastAttemptFailedRef.current = true;
        return;
      }
      const data = (await response.json()) as RainViewerApiResponse;
      // `data.radar.nowcast` is intentionally dropped — see the docblock.
      const past = data.radar.past;
      const nextTimestamps = past.map((item) => item.time);
      const nextIndex = resolveIndex(nextTimestamps, past.length);

      const rainItems = past.reduce(
        (obj, item) => {
          obj[item.time] = item.path;
          return obj;
        },
        {} as Record<number, string>,
      );

      setHost(data.host);
      setTimestamps(nextTimestamps);
      setRainRadarItems(rainItems);
      setIndex(nextIndex);
      // The playhead lands in the SAME commit as the frame list — not one
      // effect-tick later. The radar overlays mount the moment the list
      // arrives, and if currentTimestamp lags a commit behind, every overlay
      // mounts with its fetch gate shut: the map SDK's first tile burst all
      // returns null and first paint then depends on its opaque retry backoff.
      // (Nothing served: drop the playhead too, otherwise it keeps pointing at
      // a frame that no longer exists.)
      if (nextTimestamps.length === 0) {
        setCurrentTimestamp(undefined);
        setNextTimestamp(undefined);
      } else {
        setCurrentTimestamp(nextTimestamps[nextIndex]);
        setNextTimestamp(
          nextIndex < nextTimestamps.length - 1
            ? nextTimestamps[nextIndex + 1]
            : nextTimestamps[0],
        );
      }

      timestampsRef.current = nextTimestamps;
      indexRef.current = nextIndex;
      pastLengthRef.current = past.length;
      hasLoadedRef.current = true;
      lastSuccessRef.current = Dates.now();
      lastAttemptFailedRef.current = false;
    } catch (error) {
      // Silent error handling to match existing behavior
      lastAttemptFailedRef.current = true;
    } finally {
      if (watchdog) {
        clearTimeout(watchdog);
      }
      if (abortRef.current === controller) {
        abortRef.current = undefined;
      }
      // A superseded invocation must not release the guard or re-arm the timer
      // on behalf of the fetch that replaced it.
      if (fetchIdRef.current === fetchId) {
        isUpdatingRef.current = false;
        // Always stamped, including on failure: the scheduled refresh effect
        // below re-arms off this value.
        setLastDataFetch(Dates.now());
      }
    }
  }, [syncWithNow]);

  // Abort an in-flight metadata request when the hook goes away, so a
  // navigation mid-refresh doesn't leave a live watchdog behind.
  React.useEffect(
    () => () => {
      abortRef.current?.abort();
      abortRef.current = undefined;
    },
    [],
  );

  const setTimestamp = React.useCallback(
    (value: number) => {
      const newIndex = findClosestIndex(value, timestamps);
      setIndex(newIndex);
    },
    [timestamps],
  );

  React.useEffect(() => {
    if (isFocused && lastDataFetch) {
      // A failed attempt re-arms on the short retry interval, so one bad fetch
      // (typically a wake-up before connectivity is back) doesn't strand the
      // map on dropped frames for a full refresh period.
      const intervalMs = lastAttemptFailedRef.current
        ? FAILED_RETRY_INTERVAL_MS
        : REFRESH_INTERVAL_MS;
      const elapsedMs = (Dates.now() - lastDataFetch) * 1000;
      if (elapsedMs >= intervalMs) {
        fetchRadarData();
      } else {
        const remainingMs = intervalMs - elapsedMs;
        const timerId = setTimeout(() => {
          fetchRadarData();
        }, remainingMs);
        return () => clearTimeout(timerId);
      }
    }
  }, [fetchRadarData, isFocused, lastDataFetch]);

  // Refetch only if the data has aged past the refresh interval. Exposed so
  // platform code can drive it from whatever "the user came back" signal it
  // has: the web listener below uses visibilitychange/online, and React Native
  // — which has no `document` — drives it from an AppState 'active' transition
  // (see apps/mobile/src/hooks/useRefreshOnForeground.tsx).
  const refreshIfStale = React.useCallback(() => {
    const elapsedMs = (Dates.now() - lastSuccessRef.current) * 1000;
    if (elapsedMs < REFRESH_INTERVAL_MS) {
      return;
    }
    // A request still in flight from before a long suspension is worthless, and
    // the concurrency guard would otherwise swallow this wake refresh entirely
    // — leaving the user on dropped frames until the next scheduled tick.
    if (isUpdatingRef.current) {
      abortRef.current?.abort();
      abortRef.current = undefined;
      isUpdatingRef.current = false;
    }
    fetchRadarData();
  }, [fetchRadarData]);

  // Browsers throttle — and across a sleep, suspend — timers in hidden tabs, so
  // the scheduled refresh above cannot be the only recovery path: a tab restored
  // after a long absence would keep rendering timestamps the server has since
  // dropped (frames older than its retention window 404, so the radar goes
  // blank). React Native has no `document`, so this effect is a no-op there and
  // mobile uses the AppState trigger instead.
  React.useEffect(() => {
    // Reached through globalThis rather than the `document` / `window` globals
    // because the mobile tsconfig has no DOM lib.
    const view = globalThis as unknown as {
      document?: {
        visibilityState?: string;
        addEventListener?: (type: string, listener: () => void) => void;
        removeEventListener?: (type: string, listener: () => void) => void;
      };
      addEventListener?: (type: string, listener: () => void) => void;
      removeEventListener?: (type: string, listener: () => void) => void;
    };
    const doc = view.document;

    if (!isFocused || !doc?.addEventListener) {
      return;
    }

    const onWake = () => {
      // `online` can fire while still hidden — defer to the visibilitychange
      // that follows when the tab is actually restored.
      if (doc.visibilityState === 'hidden') {
        return;
      }
      refreshIfStale();
    };

    doc.addEventListener('visibilitychange', onWake);
    view.addEventListener?.('online', onWake);
    return () => {
      doc.removeEventListener?.('visibilitychange', onWake);
      view.removeEventListener?.('online', onWake);
    };
  }, [isFocused, refreshIfStale]);

  React.useEffect(() => {
    fetchRadarData();
  }, [fetchRadarData]);

  React.useEffect(() => {
    if (isFocused && timestamps.length > 0 && playing) {
      const timerId = setInterval(() => {
        setIndex((i) => (i + 1) % timestamps.length);
      }, 750);

      return () => clearInterval(timerId);
    }
  }, [timestamps, isFocused, playing]);

  useUpdateEffect(() => {
    if (isFocused && timestamps.length > 0) {
      setCurrentTimestamp(timestamps[index]);
      setNextTimestamp(
        index < timestamps.length - 1 ? timestamps[index + 1] : timestamps[0],
      );
    }
  }, [isFocused, index, timestamps]);

  return React.useMemo(
    () => ({
      currentTimestamp,
      nextTimestamp,
      timestamps,
      setTimestamp,
      rainRadarItems,
      host,
      playing,
      setPlaying,
      refreshIfStale,
    }),
    [
      currentTimestamp,
      nextTimestamp,
      timestamps,
      setTimestamp,
      rainRadarItems,
      host,
      refreshIfStale,
      playing,
      setPlaying,
    ],
  );
};

export default useRainRadar;
